<main class="fs-company">
  <header class="fs-company__topbar">
    <a [routerLink]="['/profile', companyNumber]" class="fs-company__brand">
      <span class="fs-company__brand-icon" aria-hidden="true"><i class="fal fa-building-columns"></i></span>
      <span><strong>ZINT</strong><small>Financial services</small></span>
    </a>
    <div class="fs-company__top-actions">
      <button type="button" class="fs-company__top-action" (click)="openFullProfile()">Use full profile as default <i class="fal fa-arrow-up-right" aria-hidden="true"></i></button>
    </div>
  </header>

  <div class="fs-company__content">
    @if (loading || loadingError) {
      <company-profile-loading class="fs-company__loading" [state]="loading ? 'loading' : 'error'" subtitle="Bringing together company, lending and FX signals." iconClass="fal fa-building-columns" [offeringsBand]="true"></company-profile-loading>
    } @else {
      <company-profile-hero
        class="fs-company__hero"
        eyebrow="ACCOUNT OVERVIEW"
        headingId="fs-company-name"
        [name]="company.company_overview?.name"
        [location]="company.company_overview?.registered_address_lat_long_and_zoom"
        [logoDomain]="logoDomain"
        markerFill="#8a1f3f">
        <ng-container ngProjectAs="[heroMeta]">
          <span>Company no. {{ company.company_overview?.number || companyNumber }}</span>
          @if (company.company_overview?.company_status) {<span class="fs-company__status" [class.fs-company__status--inactive]="company.company_overview.company_status !== 'Active'">{{ company.company_overview.company_status }}</span>}
          @if (company.company_overview?.latest_account_type) {<span>{{ company.company_overview.latest_account_type }}</span>}
        </ng-container>
        <div class="fs-company__hero-main">
          @if (company.company_overview?.summary) {<p class="fs-company__summary">{{ company.company_overview.summary }}</p>}
          <company-profile-contact-links class="fs-company__links" [overview]="company.company_overview"></company-profile-contact-links>
        </div>
        @if (productsAndServices.length) {
          <company-profile-offerings
            heroAside
            class="fs-company__offerings"
            role="region"
            aria-labelledby="fs-company-offerings-title"
            layout="wrap"
            heading="Products and services"
            iconClass="fal fa-box-open"
            headingId="fs-company-offerings-title"
            listId="fs-company-all-offerings"
            expandLabel="Show all"
            [items]="productsAndServices"
            [previewCount]="6"
            [showCount]="true"
            [splitDetails]="true">
          </company-profile-offerings>
        }
        <aside heroAside class="fs-company__hero-aside" aria-label="Banking relationships">
          <span class="fs-company__eyebrow">BANKING RELATIONSHIPS</span>
          @for (adviser of advisers; track adviser.role) {
            <div><span>{{ adviser.role | titlecase }} (from accounts)</span><strong>{{ adviser.name || 'Not recorded' }}</strong></div>
          }
          <div><span>Year-end reference date</span><strong>{{ company.company_overview?.financial_year_ends || 'Not recorded' }}</strong></div>
        </aside>
      </company-profile-hero>

      <section class="fs-company__facts" aria-label="Company at a glance">
        <div><span>REVENUE</span><strong>{{ company.company_overview?.revenue || '—' }}</strong><small>{{ company.company_overview?.is_revenue_estimated ? 'Estimated revenue' : 'Latest available' }}</small></div>
        <div><span>BANKING SEGMENT</span><strong>{{ segment?.label || '—' }}</strong><small>{{ segment ? segment.range + (company.company_overview?.is_revenue_estimated ? ', from estimated revenue' : '') : 'No revenue recorded' }}</small></div>
        <div><span>HEADCOUNT</span><strong>{{ company.company_overview?.headcount?.total ?? '—' }}</strong><small>{{ company.company_overview?.headcount?.is_estimated ? 'Estimated employees' : 'Employees' }}</small></div>
        <div><span>COMPANY AGE</span><strong>{{ company.company_overview?.age || '—' }}</strong><small>Registered history</small></div>
        <div><span>PRIMARY INDUSTRY</span><strong>{{ company.company_overview?.sic_codes?.[0]?.section || '—' }}</strong><small>{{ company.company_overview?.sic_codes?.[0]?.description || 'No SIC description recorded' }}</small></div>
      </section>
    }

    @if (insightsState === 'ready' && insights.credit?.groups?.length) {
      <section class="fs-company__panel fs-company__credit" aria-labelledby="fs-company-credit-title">
        <div class="fs-company__section-heading">
          <div><span class="fs-company__kind fs-company__kind--ai"><i class="fal fa-shield-check" aria-hidden="true"></i> CREDIT SCREEN · AI ANALYSIS</span><h2 id="fs-company-credit-title">{{ insights.credit!.label }}</h2></div>
        </div>
        <ng-container *ngTemplateOutlet="insightBlocks; context: { $implicit: insights.credit!.blocks }"></ng-container>
      </section>
    }

    <section class="fs-company__play-section" aria-labelledby="fs-company-view-title">
      <div class="fs-company__section-heading"><div><span class="fs-company__eyebrow">SERVICE VIEW</span><h2 id="fs-company-view-title">Focus the conversation</h2></div></div>
      @if (insights.views.length) {
        <div class="fs-company__play-list" role="group" aria-label="Choose a financial services view">
          @for (view of insights.views; track view.id) {
            <button type="button" [class.is-active]="selectedView === view.id" [attr.aria-pressed]="selectedView === view.id" (click)="selectView(view.id)">
              <strong>{{ view.label }}</strong><small>{{ view.description }}</small>
              @if (view.groups.length; as count) {<span class="fs-company__play-ai"><i class="fal fa-sparkles" aria-hidden="true"></i> {{ count }} AI {{ count === 1 ? 'analysis' : 'analyses' }}</span>}
            </button>
          }
        </div>
      }
    </section>

    <section class="fs-company__panel fs-company__ai" aria-labelledby="fs-company-ai-title" [attr.aria-busy]="insightsState === 'loading'">
      <div class="fs-company__section-heading">
        <div><span class="fs-company__kind fs-company__kind--ai"><i class="fal fa-sparkles" aria-hidden="true"></i> MAGIC INSIGHTS · AI ANALYSIS</span><h2 id="fs-company-ai-title">{{ activeView?.label || 'Financial services' }} analysis</h2></div>
        @if (insightsState === 'ready' && activeView?.groups?.length) {<span class="fs-company__ai-count">{{ activeView!.groups.length }} {{ activeView!.groups.length === 1 ? 'analysis' : 'analyses' }}</span>}
      </div>
      <p class="fs-company__ai-disclosure"><i class="fal fa-wand-magic-sparkles" aria-hidden="true"></i><span><strong>AI estimates, not recorded data.</strong> ZINT Magic Insights reads the company's accounts, website, news and job adverts to estimate needs, volumes and likelihoods. Amounts are indicative ranges and may be incomplete or wrong, so check the cited sources before relying on them.</span></p>

      @if (insightsState === 'loading') {
        <div class="fs-company__ai-state" role="status"><zint-loader size="small"></zint-loader><span>Gathering the Magic Insights analysis…</span></div>
      } @else if (insightsState === 'error') {
        <div class="fs-company__ai-state" role="alert"><i class="fal fa-triangle-exclamation" aria-hidden="true"></i><span>The AI analysis could not be loaded.</span><button type="button" (click)="retryInsights()">Try again</button></div>
      } @else if (insightsState === 'unavailable') {
        <div class="fs-company__ai-state"><i class="fal fa-lock" aria-hidden="true"></i><span>Financial Services mode is not included in your team's plan.</span></div>
      } @else if (!activeView?.groups?.length) {
        <div class="fs-company__ai-state"><i class="fal fa-sparkles" aria-hidden="true"></i><span>{{ activeView?.questionCount ? 'No AI analysis has been generated for this company and view yet.' : 'Your team does not have Magic Insights questions for this view.' }}</span></div>
      } @else {
        <ng-container *ngTemplateOutlet="insightBlocks; context: { $implicit: activeView!.blocks }"></ng-container>
      }
    </section>
  </div>

  <ng-template #insightBlocks let-blocks>
    @for (block of blocks; track block.id) {
      @if (block.group; as group) {
        <section class="fs-company__ai-group" [attr.aria-labelledby]="'fs-ai-' + group.id">
          <header class="fs-company__ai-group-head">
            <h3 [id]="'fs-ai-' + group.id">{{ group.label }}</h3>
            <span class="fs-company__ai-meta">
              <span class="fs-company__ai-confidence" title="How confident the analysis is in this answer"><i class="fas fa-circle" [style.color]="confidenceColour(group.confidence)" aria-hidden="true"></i> {{ confidenceLabel(group.confidence) }}</span>
              @if (group.generated) {<span>Analysed {{ formatInsightDate(group.generated) }}</span>}
              <button type="button" class="fs-company__ai-sources" aria-haspopup="dialog" (click)="openInsight(group)" [attr.aria-label]="'View sources for ' + group.label"><i class="fal fa-link" aria-hidden="true"></i> {{ sourceNote(group) }}</button>
            </span>
          </header>
          @if (group.unknown) {
            <p class="fs-company__ai-unknown">The analysis could not reach a conclusion for this company. Open the sources to see what was reviewed.</p>
          } @else if (!group.cards.length) {
            <p class="fs-company__ai-unknown">No answer details were returned for this question.</p>
          } @else {
            <div class="fs-company__ai-grid">
              @for (card of group.cards; track card.key) {
                <company-profile-insight-card [class]="'fs-company__ai-span-' + card.span" [insight]="card" [showConfidence]="false" [footNote]="sourceNote(group)" evidenceLabel="View sources" (viewEvidence)="openInsight(group, $event)"></company-profile-insight-card>
              }
            </div>
          }
        </section>
      } @else {
        <div class="fs-company__ai-group fs-company__ai-grid">
          @for (single of block.singles; track single.group.id) {
            <company-profile-insight-card [class]="'fs-company__ai-span-' + single.card.span" [insight]="single.card" evidenceLabel="View sources" (viewEvidence)="openInsight(single.group, $event)"></company-profile-insight-card>
          }
        </div>
      }
    }
  </ng-template>

  @if (selectedInsight && selectedInsightGroup) {
    <company-profile-evidence-dialog
      eyebrow="MAGIC INSIGHTS · AI ANALYSIS"
      [heading]="selectedInsight.title"
      [subtitle]="selectedInsightGroup.label + ' · AI estimate, check the sources below'"
      [response]="selectedInsightGroup.response"
      answerHeading="AI answer"
      commentaryHeading="AI reasoning"
      referencesHeading="Cited sources"
      closeLabel="Close sources"
      [detailedReferences]="true"
      (closed)="closeInsight()">
      <company-profile-insight-answer evidenceAnswer [insight]="selectedInsight" [expanded]="true"></company-profile-insight-answer>
    </company-profile-evidence-dialog>
  }
</main>
